import { create } from 'zustand';
import { snapTo15Min, toLogicDate, toLogicTime } from '@utils/date-utils';
import { DB } from '@services/database.service';
import { EVENT_TYPES } from '@shared/constants/event-types.v4';
import { useUIStore } from './useUIStore';
import { useHistoryStore } from './useHistoryStore';
import { useSessionStore } from './useSessionStore';
import { useProgramManifestStore } from './useProgramManifestStore';
import { EventManager } from '../../services/event-manager.service';
import { ComposerOrchestrator } from '../../services/composer-orchestrator';

interface ComposedEvent {
    id?: string;
    title: string;
    type: string;
    status: string;
    startTime: string;
    endTime: string;
    startDate: string;
    endDate: string;
    programDayId?: string | null;
    completed_session_id?: string | null;
    sleep_log_id?: string | null;
    payload: any;
    preview_data: any;
}

interface ComposerState {
    isComposerOpen: boolean;
    composedEvent: ComposedEvent | null;
}

interface ComposerActions {
    openComposer: (dateOrEvent?: Date | any, timeOverride?: any) => Promise<void>;
    closeComposer: () => void;
    updateComposedEvent: (updates: Partial<ComposedEvent>) => void;
    updateComposedPayload: (updates: any) => void;
    saveComposedEvent: (shouldClose?: boolean) => Promise<any>;
    deleteComposedEvent: () => Promise<void>;
    startSessionFromComposer: (onNavigate: (view: string) => void) => Promise<void>;
    updatePreviewData: (data: any) => void;
    updatePerformanceLog: (logId: string, field: string, value: any) => void;
    resetComposer: () => void;
}

/**
 * useComposerStore: Reliable state machine for event editing.
 * HARDENED: Uses EventManager for unified mutations and optimistic history syncing.
 */
export const useComposerStore = create<ComposerState & ComposerActions>((set, get) => ({
    isComposerOpen: false,
    composedEvent: null,

    openComposer: async (dateOrEvent = new Date(), timeOverride = null) => {
        const isExisting = dateOrEvent && typeof dateOrEvent === 'object' && !(dateOrEvent instanceof Date) && (dateOrEvent.id || dateOrEvent.start_time);
        
        if (!isExisting) {
            const date = dateOrEvent instanceof Date ? dateOrEvent : new Date();
            let snappedStart: Date, snappedEnd: Date;

            if (timeOverride?.start && timeOverride?.end) {
                snappedStart = new Date(timeOverride.start);
                snappedEnd = new Date(timeOverride.end);
            } else {
                let baseStart = new Date();
                if (timeOverride instanceof Date) baseStart = new Date(timeOverride);
                else if (timeOverride?.start) baseStart = new Date(timeOverride.start);
                else {
                    baseStart.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
                    if (date.toDateString() !== new Date().toDateString()) baseStart.setHours(8, 0, 0, 0);
                }
                snappedStart = snapTo15Min(baseStart);
                snappedEnd = new Date(snappedStart.getTime() + 60 * 60 * 1000);
            }

            set({
                isComposerOpen: true,
                composedEvent: {
                    title: timeOverride?.label || "",
                    type: (timeOverride?.type || EVENT_TYPES.PERFORMANCE).toUpperCase(), 
                    status: timeOverride?.status || 'PLANNED',
                    startTime: toLogicTime(snappedStart),
                    endTime: toLogicTime(snappedEnd),
                    startDate: toLogicDate(snappedStart),
                    endDate: toLogicDate(snappedEnd),
                    programDayId: null,
                    payload: timeOverride?.window ? { window: timeOverride.window } : {},
                    preview_data: {}
                }
            });
            return;
        }

        set({ isComposerOpen: true, composedEvent: null });
        useUIStore.getState().setIsLoading(true);

        try {
            const initialComposed = await ComposerOrchestrator.hydrateExistingEvent(dateOrEvent);
            set({ composedEvent: initialComposed });

            if (dateOrEvent.type === EVENT_TYPES.RECOVERY && dateOrEvent.sleep_log_id) {
                const { data } = await DB.fetchSleepLog(dateOrEvent.sleep_log_id);
                if (data) {
                    set(state => ({
                        composedEvent: state.composedEvent ? {
                            ...state.composedEvent,
                            payload: { ...state.composedEvent.payload, notes: data.notes, quality_rating: data.quality_rating }
                        } : null
                    }));
                }
            }
        } catch (err) {
            console.error("[ComposerStore] Hydration FAILED:", err);
            set({ composedEvent: { 
                title: "Error Loading Event", type: EVENT_TYPES.TASK, status: 'SKIPPED',
                startTime: "08:00", endTime: "09:00", startDate: toLogicDate(new Date()), endDate: toLogicDate(new Date()),
                payload: {}, preview_data: {}
            }});
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    closeComposer: () => set({ isComposerOpen: false, composedEvent: null }),

    updateComposedEvent: (updates) => set((state) => {
        if (!state.composedEvent) return state;
        const normalizedUpdates = { ...updates };
        if (normalizedUpdates.type) normalizedUpdates.type = normalizedUpdates.type.toUpperCase();
        if (normalizedUpdates.status) normalizedUpdates.status = normalizedUpdates.status.toUpperCase();
        
        const newEvent = { ...state.composedEvent, ...normalizedUpdates };
        // Removed the automatic 1-hour end-time reset. 
        // This is now managed by CompressedTemporalBar to allow independent Start/End/Duration logic.
        return { composedEvent: newEvent };
    }),

    updateComposedPayload: (updates) => set((state) => {
        if (!state.composedEvent) return state;
        return {
            composedEvent: {
                ...state.composedEvent,
                payload: { ...state.composedEvent.payload, ...updates }
            }
        };
    }),

    updatePerformanceLog: (logId, field, value) => set((state) => {
        if (!state.composedEvent) return state;
        const currentLogs = state.composedEvent.payload?.performanceLogs || [];
        const updatedLogs = currentLogs.map((log: any) => log.id === logId ? { ...log, [field]: value } : log);
        return {
            composedEvent: {
                ...state.composedEvent,
                payload: { ...state.composedEvent.payload, performanceLogs: updatedLogs },
                performanceLogs: updatedLogs
            }
        };
    }),

    updatePreviewData: (data) => set((state) => {
        if (!state.composedEvent) return state;
        return {
            composedEvent: {
                ...state.composedEvent,
                preview_data: { ...state.composedEvent.preview_data, ...data }
            }
        };
    }),

    saveComposedEvent: async (shouldClose = true) => {
        const event = get().composedEvent;
        if (!event) return;
        useUIStore.getState().setIsLoading(true);
        try {
            const res = await EventManager.saveEvent(event as any);
            // EventManager now handles optimistic sync and global refresh internally
            const savedData = res?.data;
            if (savedData?.id && !event.id) {
                set(state => ({ composedEvent: state.composedEvent ? { ...state.composedEvent, id: savedData.id } : null }));
            }
            if (shouldClose) get().closeComposer();
            return res;
        } catch (err: any) {
            console.error("[ComposerStore] Save FAILED:", err);
            alert("SYNC FAILED: " + (err.message || "Check your network connection."));
            throw err;
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    deleteComposedEvent: async () => {
        const event = get().composedEvent;
        if (!event?.id) return;
        useUIStore.getState().setIsLoading(true);
        try {
            await EventManager.deleteCitizenEvent(event.id, event.sleep_log_id || event.completed_session_id || undefined, event.type);
            get().closeComposer();
        } catch (err) {
            console.error("[ComposerStore] Delete FAILED:", err);
            throw err;
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    startSessionFromComposer: async (onNavigate) => {
        const initialEvent = get().composedEvent;
        if (!initialEvent?.programDayId) return;
        useUIStore.getState().setIsLoading(true);
        try {
            await get().saveComposedEvent(false);
            const savedEvent = get().composedEvent;
            const isToday = new Date(savedEvent!.startDate).toDateString() === new Date().toDateString();
            const dateArg = isToday ? null : new Date(`${savedEvent!.startDate}T${savedEvent!.startTime}:00`).toISOString();
            await useSessionStore.getState().startSession(savedEvent!.programDayId!, dateArg, savedEvent!.id);
            get().closeComposer();
            onNavigate('session');
        } catch (err) {
            console.error("[ComposerStore] startSessionFromComposer FAILED:", err);
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    resetComposer: () => set({ isComposerOpen: false, composedEvent: null })
}));

if (typeof window !== 'undefined') {
    (window as any).useComposerStore = useComposerStore;
}
